import { useEffect, useRef, useState } from "react";
import { ScrubField } from "./ScrubField";
import { ScrubInput } from "./ScrubInput";
import { formatNumberInput, parseNumericInput } from "./numberFormatting";

export function NumberField({
  label,
  value,
  mixed = false,
  min,
  max,
  step = 1,
  onCommit,
}: {
  label: string;
  value: number;
  mixed?: boolean;
  min: number;
  max: number;
  step?: number;
  onCommit: (value: number) => void;
}) {
  const committed = mixed ? "Mixed" : formatNumberInput(value);
  const [draft, setDraft] = useState(committed);
  const [editing, setEditing] = useState(false);
  const skipBlurCommitRef = useRef(false);

  useEffect(() => {
    if (!editing) {
      setDraft(committed);
    }
  }, [committed, editing]);

  function commit(nextDraft = draft): void {
    const parsed = parseNumericInput(nextDraft);
    if (parsed !== undefined) {
      const clamped = Math.min(max, Math.max(min, parsed));
      onCommit(clamped);
      setDraft(formatNumberInput(clamped));
      return;
    }

    setDraft(committed);
  }

  function updateValue(nextValue: number): void {
    const clamped = Math.min(max, Math.max(min, nextValue));
    setDraft(formatNumberInput(clamped));
    onCommit(clamped);
  }

  return (
    <ScrubField label={label} scrubValue={value} scrubMin={min} scrubMax={max} scrubStep={step} onScrubChange={updateValue}>
      <ScrubInput
        type="text"
        inputMode="decimal"
        data-numeric
        min={min}
        max={max}
        step={step}
        value={draft}
        onFocus={() => setEditing(true)}
        onChange={event => {
          setDraft(event.target.value);
        }}
        onBlur={() => {
          setEditing(false);
          if (skipBlurCommitRef.current) {
            skipBlurCommitRef.current = false;
            return;
          }
          commit();
        }}
        onKeyDown={event => {
          if (event.key === "Enter") {
            event.preventDefault();
            setEditing(false);
            event.currentTarget.blur();
          }
          if (event.key === "Escape") {
            event.preventDefault();
            skipBlurCommitRef.current = true;
            setDraft(committed);
            setEditing(false);
            event.currentTarget.blur();
          }
          if (event.key === "ArrowUp" || event.key === "ArrowDown") {
            event.preventDefault();
            const current = parseNumericInput(draft);
            const fallback = event.key === "ArrowUp" ? min : Math.min(max, min + step);
            const direction = event.key === "ArrowUp" ? 1 : -1;
            updateValue((current !== undefined ? current : fallback) + direction * step);
          }
        }}
        className={mixed ? "text-slate-400" : undefined}
      />
    </ScrubField>
  );
}
